import { useState } from 'react'
import { Star } from 'lucide-react'
import { Banner, Button, Field, IconButton, Row, Sheet } from '../ui'
import { attempt, useWriteError } from '../lib/useAction'
import { signalFeatures } from '../lib/entries'
import { rowTitle } from '../lib/rowMeta'
import { useMarkSeen, useUnmarkSeen, type SeenRow } from '../lib/seen'
import type { FeedRow } from '../lib/types'

const STARS = [1, 2, 3, 4, 5] as const

/**
 * WP 8.4: "seen it, and a rating out of five" — opened from `EventActions`'s
 * new row, on any card's long press. Conditionally rendered by its caller
 * (the same shape `EventActions`' own "wrong" sheet uses) rather than always
 * mounted with an `open` toggle: it needs no state that survives being
 * closed, so there is nothing an early unmount would lose.
 *
 * `ids` is every entry a merged film card stands for (`idsOf`, computed by
 * the caller): the same reasoning `forEachEntry` exists for elsewhere in this
 * app — rating one week of a run and being asked again next week is not a
 * thing anyone wants, so a rating is written to every id at once, stopping at
 * the first failure.
 *
 * `seen` is the existing row for `e.id`, where there is one — reopening a
 * rated event shows the stars already set, and offers "Not seen after all"
 * rather than a second way to say the same thing Save already does.
 */
export function RateSheet({ e, ids, seen, onClose }: {
  e: FeedRow
  ids: string[]
  seen: SeenRow | null
  onClose: () => void
}) {
  const [stars, setStars] = useState<number | null>(seen?.stars ?? null)
  // Separate from `stars` itself: null means both "not decided yet" and "seen,
  // no rating", and Save has to tell the two apart. Starts true when reopening
  // an already-seen row — there is nothing left to decide, only to change.
  const [touched, setTouched] = useState(!!seen)
  const [note, setNote] = useState('')

  const markM = useMarkSeen()
  const unmarkM = useUnmarkSeen()
  const error = useWriteError(markM, unmarkM)

  const pick = (n: number) => { setStars(n); setTouched(true) }
  const pickUnrated = () => { setStars(null); setTouched(true) }

  async function save() {
    const trimmed = note.trim() || null
    for (const id of ids) {
      const ok = await attempt(markM.mutateAsync({
        entryId: id, stars, features: signalFeatures(e), note: trimmed,
      }))
      if (!ok) return
    }
    onClose()
  }

  async function notSeenAfterAll() {
    for (const id of ids) {
      const ok = await attempt(unmarkM.mutateAsync(id))
      if (!ok) return
    }
    onClose()
  }

  return (
    <Sheet
      open onOpenChange={o => { if (!o) onClose() }} title={rowTitle(e)} closeButton
      actions={
        <>
          <Button variant="primary" disabled={!touched || markM.isPending} onClick={() => void save()}>
            Save
          </Button>
          {seen && (
            <Button variant="quiet" disabled={unmarkM.isPending} onClick={() => void notSeenAfterAll()}>
              Not seen after all
            </Button>
          )}
        </>
      }
    >
      {error && <Banner tone="bad">{error}</Banner>}
      <div className="rate__stars">
        {STARS.map(n => (
          <IconButton
            key={n}
            label={`${n} star${n === 1 ? '' : 's'}`}
            className={`rate__star${stars != null && n <= stars ? ' is-on' : ''}`}
            onClick={() => pick(n)}
          >
            <Star size={24} fill={stars != null && n <= stars ? 'currentColor' : 'none'} />
          </IconButton>
        ))}
      </div>
      <Row
        className="u-row--list"
        leading={null}
        title="Seen, no rating"
        meta={null}
        onPress={pickUnrated}
        trailing={touched && stars === null ? <span aria-hidden="true">✓</span> : null}
      />
      <Field label="Note (optional)">
        <Field.Input value={note} placeholder="A word about it" onChange={ev => setNote(ev.target.value)} />
      </Field>
    </Sheet>
  )
}
